/* Copyright (c) 2022-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, ReplaySubject, Subscription, throwError } from 'rxjs';
import { catchError, debounceTime, finalize, map, share, switchMap, take, tap } from 'rxjs/operators';
import { isEqual } from 'lodash';
import { Commands } from 'bridge-commands/commands';
import { SingleEventStream } from 'utils/lib/event-stream/event-streams';
import { SingleEventStreamFactoryService } from 'utils/lib/event-stream-factory/event-stream-factory.service';
import { BridgeService } from '../bridge/bridge.service';
import { Logger, LoggingService } from '../logging/logging.service';
import { SystemInfoKey } from '../system-info-service/system-info.models';
import { SystemInfoService } from '../system-info-service/system-info.service';
import { WindowService } from '../window/window.service';
import {
    defaultHardwareRefreshDebounceTime,
    GenericInfo,
    GPUInfo,
    LaptopSpecificMoboTypeList,
    SLIInfo,
    HardwareGPU
} from './models/hardware-information';

@Injectable({
    providedIn: 'root'
})
export class HardwareInformationService {
    private readonly logger: Logger;
    private gpuInfoStream: SingleEventStream<GPUInfo>;
    private genericInfoStream: SingleEventStream<GenericInfo>;
    private gpuInfoObs$: Observable<GPUInfo>;
    private genericInfoObs$: Observable<GenericInfo>;
    private onFocusInfoRefreshSub: Subscription;

    constructor(
        private loggingService: LoggingService,
        private bridgeService: BridgeService,
        private windowService: WindowService,
        private eventStreamFactory: SingleEventStreamFactoryService,
        private systemInfoService: SystemInfoService
    ) {
        this.logger = this.loggingService.getLogger('HardwareInformationService');
        this.gpuInfoStream = this.eventStreamFactory.createStream<GPUInfo>(() => this.fetchGPUInfo());
        this.genericInfoStream = this.eventStreamFactory.createStream<GenericInfo>(() => this.fetchGenericInfo());
        this.setupHardwareInfoRefreshOnAppWindowFocus();
    }

    public getGenericInfo(refresh: boolean): Observable<GenericInfo> {
        if (refresh) {
            return this.genericInfoStream.refresh();
        }
        return this.genericInfoStream.get$();
    }

    public getGpuInfo(refresh: boolean): Observable<GPUInfo> {
        if (refresh) {
            return this.gpuInfoStream.refresh();
        }
        return this.gpuInfoStream.get$();
    }

    public getSystemArchitecture(): Observable<string> {
        return this.systemInfoService.fetchSystemArchitecture();
    }

    public getSLIInformation(refresh = true): Observable<SLIInfo> {
        return this.getGpuInfo(refresh).pipe(map(gpuInfo => gpuInfo.SLI));
    }

    public getOptimusStatus(refresh = true): Observable<string> {
        return this.getGpuInfo(refresh).pipe(map(gpuInfo => gpuInfo.IsOptimus));
    }

    public getIsLaptopSystem(): Observable<boolean> {
        return this.getGenericInfo(false).pipe(
            map(genericInfo => LaptopSpecificMoboTypeList.includes(genericInfo?.MoboType)),
            catchError(_ => of(false))
        );
    }

    public fetchHardwareInfo(): Observable<[GenericInfo, GPUInfo]> {
        return combineLatest([this.fetchGenericInfo(), this.fetchGPUInfo()]).pipe(
            take(1),
            tap(([genericInfo, gpuInfo]) => {
                const currentGenericData = this.genericInfoStream.getData();
                const currentGpuData = this.gpuInfoStream.getData();
                if (!isEqual(currentGenericData, genericInfo)) {
                    this.genericInfoStream.setData(genericInfo, true);
                }
                if (!isEqual(currentGpuData, gpuInfo)) {
                    this.gpuInfoStream.setData(gpuInfo, true);
                }
            })
        );
    }

    public getQuadroSystemFlag(): Observable<boolean> {
        return this.getGpuInfo(false).pipe(
            map(gpuInfo => {
                const isPrimaryGpu = gpuInfo?.GPU?.filter(gpu => gpu.IsPrimary);
                const isQuadro = +isPrimaryGpu?.[0]?.IsQuadro;
                return isQuadro === 1;
            }),
            catchError(error => {
                this.logger.error('failed to get quadro system flag', error);
                return of(false);
            })
        );
    }

    /**
     * Gets the first Blackwell GPU found in the system from cached data (synchronous)
     * @returns Blackwell GPU info if found in cache, null otherwise
     */
    public getBlackwellGpuInSystemSync(): HardwareGPU | null {
        const cachedGpuInfo = this.gpuInfoStream.getData();
        if (!cachedGpuInfo?.GPU || cachedGpuInfo.GPU.length === 0) {
            return null;
        }

        // Find the first Blackwell GPU in the cached system data
        const blackwellGpu = cachedGpuInfo.GPU.find(gpu => this.isBlackwellArchitecture(gpu.GPUArchitecture));
        if (!blackwellGpu) {
            return null;
        }
        return blackwellGpu;
    }

    /**
     * Checks if the given GPU architecture represents Blackwell architecture
     * @param architecture GPU architecture string value
     * @returns true if it's Blackwell architecture
     */
    public isBlackwellArchitecture(architecture: string): boolean {
        if (!architecture) {
            return false;
        }

        // Convert architecture string to number for comparison
        const archNumber = parseInt(architecture, 10);

        // Blackwell architecture starts from 416
        const BLACKWELL_ARCHITECTURE = 416;
        return archNumber >= BLACKWELL_ARCHITECTURE;
    }

    private setupHardwareInfoRefreshOnAppWindowFocus(debounce?: number) {
        this.onFocusInfoRefreshSub?.unsubscribe();
        this.onFocusInfoRefreshSub = this.windowService
            .getFocusEvent()
            .pipe(
                debounceTime(debounce ?? defaultHardwareRefreshDebounceTime),
                switchMap(_ => {
                    this.logger.info('hardware info refreshed on focus');
                    // switchMap to cancel and start a new request
                    return this.fetchHardwareInfo();
                }),
                catchError(err => {
                    // we want a long-living observable, hence we catch any errors and return bool
                    this.logger.error('failed to reload sys info', err);
                    return of(true);
                })
            )
            .subscribe();
    }

    private fetchGenericInfo(): Observable<GenericInfo> {
        if (this.genericInfoObs$) {
            return this.genericInfoObs$;
        }

        this.genericInfoObs$ = this.bridgeService
            .executeCommand(Commands.Nvapp.Driverupdaterecommendation.GetGenericInfo)
            .pipe(
                share(),
                tap(genericInfo => {
                    this.logger.info('Generic info fetched success:', genericInfo);
                }),
                catchError(error => {
                    this.logger.error('there is problem fetching generic info', error);
                    return throwError(error);
                }),
                finalize(() => {
                    this.genericInfoObs$ = null;
                })
            );
        return this.genericInfoObs$;
    }

    private fetchGPUInfo(): Observable<GPUInfo> {
        if (this.gpuInfoObs$) {
            return this.gpuInfoObs$;
        }
        this.gpuInfoObs$ = this.bridgeService.executeCommand(Commands.Nvapp.Driverupdaterecommendation.GetGPUInfo).pipe(
            share(),
            tap(gpuInfo => {
                this.logger.info('Gpu info fetched success:', gpuInfo);
            }),
            catchError(error => {
                this.logger.error('there is problem fetching gpu info', error);
                return throwError(error);
            }),
            finalize(() => {
                this.gpuInfoObs$ = null;
            })
        );
        return this.gpuInfoObs$;
    }
}
